import React from "react";
import { Award, Droplets, Star, Paintbrush2 } from "lucide-react";
import { useAnalytics } from "../hooks/useAnalytics";

const ProfitecProductsSectionSimple = () => {
  const { trackWhatsAppClick } = useAnalytics();

  // Handle WhatsApp click with conversion tracking
  const handleWhatsAppClick = (source) => {
    trackWhatsAppClick(source);
    // Link will naturally open via href - no need to manually open
  };

  return (
    <section className="py-8 md:py-16 bg-gradient-to-br from-gray-50 via-blue-50 to-orange-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Simple Header Test */}
        <div className="text-center mb-12">
          <div className="mb-6">
            {/* Brand Badge */}
            <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-lg border border-gray-100 mb-4">
              <Paintbrush2 className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-600">Deutsche Markenfarben</span>
            </div>
            
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              <span className="bg-gradient-to-r from-blue-600 to-orange-500 bg-clip-text text-transparent">
                PROFITEC
              </span>{" "}
              <span className="text-gray-700">Profi-Farben</span>
              <Paintbrush2 className="inline-block w-8 h-8 ml-3 text-blue-600" />
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Hochwertige deutsche Farben für perfekte Malerarbeiten in Baden-Württemberg
            </p>
          </div>
          
          <div className="flex items-center justify-center gap-6 text-sm text-gray-600 flex-wrap">
            <div className="flex items-center gap-2">
              <Award className="h-4 w-4 text-blue-600" />
              <span>Premium Qualität</span>
            </div>
            <div className="flex items-center gap-2">
              <Droplets className="h-4 w-4 text-blue-600" />
              <span>Profi-Ergebnisse</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="h-4 w-4 text-yellow-500" />
              <span>Made in Germany</span>
            </div>
          </div>
        </div>

        {/* Simple Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {[
            {
              name: "Rekord PRO",
              code: "P116",
              description: "Ultramatte Innenfarbe",
              features: ["Leichte Verarbeitung", "Ausbesserungsfähig", "Streiflichtreduziert"]
            },
            {
              name: "Superweiß",
              code: "P120", 
              description: "Die Innenfarbe mit bestem Deckvermögen",
              features: ["Hervorragende Verarbeitung", "Hoher Weißgrad", "Strukturerhaltend"]
            },
            {
              name: "Rapid Plus",
              code: "P118+",
              description: "Allround-Einschicht-Innenfarbe", 
              features: ["Für Flächen mit Streiflicht", "Sehr gute Ausbesserung", "Hervorragende Verarbeitung"]
            }
          ].map((product, index) => (
            <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="bg-gradient-to-r from-blue-600 to-orange-500 text-white px-4 py-2 rounded-t-lg -mx-6 -mt-6 mb-4">
                <div className="flex justify-between items-center">
                  <span className="font-bold">{product.name}</span>
                  <span className="text-xs font-mono">{product.code}</span>
                </div>
              </div>
              
              <h3 className="text-lg font-bold text-gray-900 mb-2">{product.description}</h3>
              
              <ul className="space-y-2 mb-4">
                {product.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    {feature}
                  </li>
                ))}
              </ul>
              
              <a
                href="https://wa.me/4915758183212?text=Hallo,%20ich%20interessiere%20mich%20für%20PROFITEC%20Farben."
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleWhatsAppClick(`profitec_product_${product.code}`)}
                className="block text-center bg-gradient-to-r from-blue-600 to-orange-500 text-white py-2 px-4 rounded-lg hover:from-blue-700 hover:to-orange-600 transition-all duration-200 text-sm font-medium"
              >
                📞 Angebot anfordern
              </a>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-blue-600 to-orange-500 rounded-xl p-6 text-white">
            <h3 className="text-xl font-bold mb-2">Professionelle PROFITEC Farben für Ihre Projekte</h3>
            <p className="mb-4 text-blue-100">
              Wir verwenden ausschließlich hochwertige PROFITEC Farben für alle Malerarbeiten in Baden-Württemberg
            </p>
            <a
              href="https://wa.me/4915758183212?text=Hallo,%20ich%20möchte%20ein%20Angebot%20für%20Malerarbeiten%20mit%20PROFITEC%20Qualitätsfarben."
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleWhatsAppClick('profitec_cta_bottom')}
              className="inline-flex items-center gap-2 bg-white text-blue-600 px-6 py-3 rounded-lg hover:bg-gray-100 transition-colors font-medium"
            >
              <span>🎨</span>
              Kostenlos beraten lassen
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProfitecProductsSectionSimple;